import {
  ALL_SENSOR_TYPES,
  type AamsRegistrationFormInput,
  type AamsRegistrationFormOutput,
  AnsielRegistrationFormInput,
  type AnsielRegistrationFormOutput,
  DELETION_FLAG,
  type FacilityKey,
  SENSOR_TYPE,
  getAamsRegistrationFormSchema,
  getAnsielRegistrationFormSchema,
  parse,
} from "@packages/core";
import { useForm, useStore } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { type Merge } from "type-fest";
import {
  Button,
  DropDownList,
  FormFieldContainer,
  FormFieldInput,
  Input,
  PageName,
  ProviderSelector,
  buttonStyles,
  formFieldInputStyles,
  showError,
} from "#components";
import {
  $alert,
  $confirm,
  createSensor,
  useGetErrorMessage,
  useProviderInfiniteQuery,
  useTranslate,
} from "#services";
import { css, cx } from "#styled-system/css";

export const Route = createFileRoute(
  "/_authenticated/facilities/$facilityKey/sensors/registration",
)({ component: Component });

function Component() {
  const { facilityKey } = Route.useParams();

  const navigate = useNavigate();
  const translate = useTranslate();

  const [sensorType, setSensorType] = useState(SENSOR_TYPE.AAMS);

  const handleSubmit = useCallback(
    async function (output: AamsRegistrationFormOutput | AnsielRegistrationFormOutput) {
      try {
        console.debug("フォームが送信されました。", output);
        const { sensor } = await createSensor({
          ...output,
          facilityKey: output.facility?.facilityKey,
          organizationKey: output.organization?.organizationKey,
          providerKey: output.provider.providerKey,
        });

        await $alert({
          color: "success",
          content: translate("%Register.2%", { $: { name: sensor.name } }),
          title: translate("%Register.0%"),
        });

        await navigate({
          ignoreBlocker: true,
          params: { facilityKey },
          to: "/facilities/$facilityKey/sensors",
        });
      } catch (error) {
        await showError(error);
      }
    },
    [facilityKey, navigate, translate],
  );

  return (
    <main className={css({ flex: 1, paddingX: "32px", paddingY: "24px" })}>
      <PageName>{translate("%SensorInformation.0%")}</PageName>
      <p className={css({ fontSize: "24px", fontWeight: 700 })}>
        {translate("%SensorInformation.0%")}
      </p>

      <DropDownList
        className={css({ backgroundColor: "white", marginTop: "16px", maxWidth: "320px" })}
        onChange={function (event) {
          setSensorType(event.target.value);
        }}
        value={sensorType}
      >
        {ALL_SENSOR_TYPES.map(function (sensorType) {
          return (
            <option key={sensorType} value={sensorType}>
              {translate("%SensorType." + sensorType + ".0%")}
            </option>
          );
        })}
      </DropDownList>

      {sensorType === SENSOR_TYPE.AAMS ? (
        <AamsRegistrationForm facilityKey={facilityKey} onSubmit={handleSubmit} />
      ) : sensorType === SENSOR_TYPE.ANSIEL ? (
        <AnsielRegistrationForm facilityKey={facilityKey} onSubmit={handleSubmit} />
      ) : (
        <p className={css({ marginTop: "16px" })}>{translate("%z%")}</p>
      )}
    </main>
  );
}

// =============================================================
// aams登録フォーム
// =============================================================

type AamsRegistrationFormProperties = Merge<
  React.ComponentProps<"form">,
  {
    facilityKey: FacilityKey;
    onSubmit: { (output: AamsRegistrationFormOutput): Promise<void> };
  }
>;
function AamsRegistrationForm({
  className,
  facilityKey,
  onSubmit,
  ...properties
}: AamsRegistrationFormProperties) {
  const getErrorMessage = useGetErrorMessage();
  const translate = useTranslate();

  const schema = getAamsRegistrationFormSchema();
  const defaultValues = useMemo(function (): AamsRegistrationFormInput {
    return {
      facility: null,
      macAddress: "",
      name: "",
      organization: null,
      provider: null,
      type: SENSOR_TYPE.AAMS,
    };
  }, []);

  const { Field, Subscribe, handleSubmit, ...form } = useForm({
    defaultValues,
    async onSubmit({ value: input }) {
      const output = parse(schema, input);
      await onSubmit(output);
    },
    validators: { onSubmit: schema },
  });

  const organization = useStore(form.store, (state) => state.values.organization);

  {
    const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue);
    const isSubmitted = useStore(form.store, (state) => state.isSubmitted);
    const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
    useBlocker({
      async shouldBlockFn() {
        return (!isDefaultValue && !isSubmitted) || isSubmitting
          ? !(await $confirm({
              color: "warning",
              content: translate("%Form.1%"),
              no: translate("%Stay.0%"),
              title: translate("%Form.0%"),
              yes: translate("%Leave.0%"),
            }))
          : false;
      },
      withResolver: true,
    });
  }

  const providerPaginator = useProviderInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });

  useEffect(
    function () {
      // 有効なデータが対象の場合、全て取得する。
      if (providerPaginator.hasNextPage && !providerPaginator.isFetching) {
        providerPaginator.fetchNextPage();
      }
    },
    [providerPaginator],
  );

  const providers = useMemo(
    function () {
      return (
        providerPaginator.data?.pages.flatMap(function (page) {
          return page.providers;
        }) ?? []
      );
    },
    [providerPaginator.data],
  );

  return (
    <form
      {...properties}
      className={cx(
        css({
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          marginTop: "16px",
        }),
        className,
      )}
      onSubmit={function (event) {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <Field
        children={function (field) {
          return (
            <Input
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              className={css({ maxWidth: "320px" })}
              id={field.name}
              label={translate("%SensorName.0%")}
              onBlur={() => field.handleBlur()}
              onChange={(event) => field.handleChange(event.target.value)}
              required
              title=""
              value={field.state.value}
            />
          );
        }}
        name="name"
      />
      <Field
        children={function (field) {
          return (
            <Input
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              className={css({ maxWidth: "320px" })}
              id={field.name}
              label={translate("%MacAddress.0%")}
              onBlur={() => field.handleBlur()}
              onChange={(event) => field.handleChange(event.target.value)}
              required
              title=""
              value={field.state.value}
            />
          );
        }}
        name="macAddress"
      />
      <Field
        children={function (field) {
          return (
            <FormFieldContainer
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              invalid={field.state.meta.errors.length > 0}
              label={
                translate("%Provider.0%") + (organization ? " (" + organization.name + ")" : "")
              }
            >
              <ProviderSelector
                className={css({ maxWidth: "320px", width: "100%" })}
                load={async function () {
                  await providerPaginator.fetchNextPage();
                }}
                loaded={!providerPaginator.hasNextPage}
                loading={providerPaginator.isLoading}
                onBlur={field.handleBlur}
                onChange={function (provider) {
                  field.handleChange(provider);
                }}
                options={providers}
                title={translate("%Provider.0%")}
                value={field.state.value}
              />
            </FormFieldContainer>
          );
        }}
        name="provider"
      />

      <div className={css({ display: "flex", gap: "8px" })}>
        <Subscribe
          selector={function ({ isSubmitting }) {
            return isSubmitting;
          }}
        >
          {function (disabled) {
            return (
              <Link
                className={buttonStyles({ color: "secondary" })}
                disabled={disabled}
                params={{ facilityKey }}
                to="/facilities/$facilityKey/sensors"
              >
                {translate("%Cancel.0%")}
              </Link>
            );
          }}
        </Subscribe>
        <Subscribe
          selector={function ({ canSubmit, isSubmitting }) {
            return !canSubmit || isSubmitting;
          }}
        >
          {function (disabled) {
            return (
              <Button className={css({ width: "max-content" })} disabled={disabled} type="submit">
                {translate("%Save.0%")}
              </Button>
            );
          }}
        </Subscribe>
      </div>
    </form>
  );
}

// =============================================================
// ANSIEL登録フォーム
// =============================================================

type AnsielRegistrationFormProperties = Merge<
  React.ComponentProps<"form">,
  {
    facilityKey: FacilityKey;
    onSubmit: { (output: AnsielRegistrationFormOutput): Promise<void> };
  }
>;
function AnsielRegistrationForm({
  className,
  facilityKey,
  onSubmit,
  ...properties
}: AnsielRegistrationFormProperties) {
  const getErrorMessage = useGetErrorMessage();
  const translate = useTranslate();

  const schema = getAnsielRegistrationFormSchema();
  const defaultValues = useMemo(function (): AnsielRegistrationFormInput {
    return {
      facility: null,
      name: "",
      organization: null,
      provider: null,
      pushNotificationEnabled: false,
      serialNumber: "",
      type: SENSOR_TYPE.ANSIEL,
    };
  }, []);

  const { Field, Subscribe, handleSubmit, ...form } = useForm({
    defaultValues,
    async onSubmit({ value: input }) {
      const output = parse(schema, input);
      await onSubmit(output);
    },
    validators: { onSubmit: schema },
  });

  const organization = useStore(form.store, (state) => state.values.organization);

  {
    const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue);
    const isSubmitted = useStore(form.store, (state) => state.isSubmitted);
    const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
    useBlocker({
      async shouldBlockFn() {
        return (!isDefaultValue && !isSubmitted) || isSubmitting
          ? !(await $confirm({
              color: "warning",
              content: translate("%Form.1%"),
              no: translate("%Stay.0%"),
              title: translate("%Form.0%"),
              yes: translate("%Leave.0%"),
            }))
          : false;
      },
      withResolver: true,
    });
  }

  const providerPaginator = useProviderInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });

  useEffect(
    function () {
      // 有効なデータが対象の場合、全て取得する。
      if (providerPaginator.hasNextPage && !providerPaginator.isFetching) {
        providerPaginator.fetchNextPage();
      }
    },
    [providerPaginator],
  );

  const providers = useMemo(
    function () {
      return (
        providerPaginator.data?.pages.flatMap(function (page) {
          return page.providers;
        }) ?? []
      );
    },
    [providerPaginator.data],
  );

  return (
    <form
      {...properties}
      className={cx(
        css({
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          marginTop: "16px",
        }),
        className,
      )}
      onSubmit={function (event) {
        event.preventDefault();
        handleSubmit();
      }}
    >
      <Field
        children={function (field) {
          return (
            <Input
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              className={css({ maxWidth: "320px" })}
              id={field.name}
              label={translate("%SensorName.0%")}
              onBlur={() => field.handleBlur()}
              onChange={(event) => field.handleChange(event.target.value)}
              required
              title=""
              value={field.state.value}
            />
          );
        }}
        name="name"
      />
      <Field
        children={function (field) {
          return (
            <FormFieldContainer
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              className={css({ maxWidth: "320px" })}
              description={translate("%SerialNumber.1%")}
              invalid={!field.state.meta.isValid}
              label={translate("%SerialNumber.0%")}
              required
            >
              <FormFieldInput
                className={css(formFieldInputStyles.raw({}), {
                  backgroundColor: "white",
                  width: "100%",
                })}
                id={field.name}
                onBlur={function () {
                  field.handleBlur();
                }}
                onChange={function (event) {
                  field.handleChange(event.target.value);
                }}
                value={field.state.value}
              />
            </FormFieldContainer>
          );
        }}
        name="serialNumber"
      />
      <Field
        children={function (field) {
          return (
            <FormFieldContainer
              caution={getErrorMessage(field.state.meta.errors.at(0))}
              invalid={field.state.meta.errors.length > 0}
              label={
                translate("%Provider.0%") + (organization ? " (" + organization.name + ")" : "")
              }
            >
              <ProviderSelector
                className={css({ maxWidth: "320px", width: "100%" })}
                load={async function () {
                  await providerPaginator.fetchNextPage();
                }}
                loaded={!providerPaginator.hasNextPage}
                loading={providerPaginator.isLoading}
                onBlur={field.handleBlur}
                onChange={function (provider) {
                  field.handleChange(provider);
                }}
                options={providers}
                title={translate("%Provider.0%")}
                value={field.state.value}
              />
            </FormFieldContainer>
          );
        }}
        name="provider"
      />

      <div className={css({ display: "flex", gap: "8px" })}>
        <Subscribe
          selector={function ({ isSubmitting }) {
            return isSubmitting;
          }}
        >
          {function (disabled) {
            return (
              <Link
                className={buttonStyles({ color: "secondary" })}
                disabled={disabled}
                params={{ facilityKey }}
                to="/facilities/$facilityKey/sensors"
              >
                {translate("%Cancel.0%")}
              </Link>
            );
          }}
        </Subscribe>
        <Subscribe
          selector={function ({ canSubmit, isSubmitting }) {
            return !canSubmit || isSubmitting;
          }}
        >
          {function (disabled) {
            return (
              <Button className={css({ width: "max-content" })} disabled={disabled} type="submit">
                {translate("%Save.0%")}
              </Button>
            );
          }}
        </Subscribe>
      </div>
    </form>
  );
}
